import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import { Icons } from "../../../../assets/icons";
import { ReactComponent as BackIcon } from "../../../../assets/icons/back.svg";
import {
  ZERO,
  TEN,
  TRUE,
  FALSE,
  messages,
  ALL_EVENT,
  EMPTY_STRING,
  localStorageKeys,
  ADD_RECAPZ_STEPS,
  eventBookingStatus,
} from "../../../../constants";
import { saveToLocalStorage, getLocalStorageItem } from "../../../../system";
import {
  customToast,
  isArrayWithItems,
  isAddRecapzIdentical,
  getReOrderedRecapList,
  generateUniqueIdForReels,
  transformReelsInEditEvent,
} from "../../../../shared";
import CustomTooltip from "../../../../shared/components/ToolTip/CustomTooltip";
import ConfirmationModal from "../../../../shared/components/Modal/ConfirmationModal";
import MultiSelectDropdown from "../../../../shared/components/Fields/MultiSelectDropdown";
import InputGroupWrapper from "../../../../shared/components/InputGroup/InputGroupWrapper";
import { eventBookingActions } from "../../../../redux/actions";

const { ChevronDownIcon } = Icons;

// Returns { deliveredCount, hasDelivered } for a given eventId from editEvents.reels
const getDeliveredReelInfo = (eventId, editEvents) => {
  const reels = editEvents?.reels ?? [];
  const isCombined = eventId === ALL_EVENT.id;

  const matching = reels.filter((r) =>
    isCombined
      ? r.isCombinedEventsReel
      : !r.isCombinedEventsReel &&
        r.eventOrderItemMap?.some((m) => m.eventId === eventId)
  );

  const deliveredCount = matching.filter((r) => r.isReelDelivered).length;
  return { deliveredCount, hasDelivered: deliveredCount > 0 };
};

// Returns how many reels are already booked for a given eventId, counting
// only reels that were NOT themselves added through the add-recap flow
// (isAddFlowReel is false or undefined). Reels added in this same session
// are pending additions, not part of the baseline we cap against.
const getBookedReelCount = (eventId, editEvents) => {
  const reels = editEvents?.reels ?? [];
  const isCombined = eventId === ALL_EVENT.id;

  return reels.filter(
    (r) =>
      (isCombined
        ? r.isCombinedEventsReel
        : !r.isCombinedEventsReel &&
          r.eventOrderItemMap?.some((m) => m.eventId === eventId)) &&
      (r.isAddFlowReel === false || r.isAddFlowReel === undefined)
  ).length;
};

const SelectEventQuantity = (props) => {
  const { t } = useTranslation("common");

  const {
    handleActiveStep,
    formState,
    addRecapz,
    setAddRecapz,
    isAddUpdateRecap,
    isUpdateRecap,
  } = props;

  const [activatedEvents, setActivatedEvents] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [recapBriefConfirmModalMessage, setRecapBriefConfirmModalMessage] =
    useState(EMPTY_STRING);

  const isAddFlow = isAddUpdateRecap && !isUpdateRecap;

  useEffect(() => {
    const removedSnapshot = getLocalStorageItem(
      localStorageKeys.ADD_CANCELLED_RECAPZ,
      []
    );
    if (!removedSnapshot.length) return;

    const merged = addRecapz.map((item) => {
      const snapshottedRemoved =
        removedSnapshot.find((x) => x.eventId === item.eventId)?.reelDetail ??
        [];

      if (!snapshottedRemoved.length) return item;

      const currentReels = item.reelDetail ?? [];

      // Merge back, avoiding duplicates by id
      const mergedReels = [
        ...currentReels,
        ...snapshottedRemoved.filter(
          (sr) => !currentReels.some((r) => r.id === sr.id)
        ),
      ];

      return { ...item, reelDetail: mergedReels };
    });

    setAddRecapz(merged);
  }, []);

  const handleQuantityChange = (eventId, value) => {
    let quantity = parseInt(value, TEN);

    const updated = [...addRecapz];
    const index = updated.findIndex((x) => x.eventId === eventId);

    const defaultRecapType = props.recapTypes[0]?.name || EMPTY_STRING;
    const defaultRecapPrice = props.recapTypes[0]?.price || ZERO;

    if (index !== -1) {
      const fullRecap = Array.isArray(updated[index].reelDetail)
        ? [...updated[index].reelDetail]
        : [];

      // In the add-recap flow, the quantity control only governs reels that
      // were themselves added through this flow (isAddFlowReel === true).
      // Pre-existing reels (booked outside this flow) are left completely
      // untouched and simply re-appended as-is on every branch below.
      const existingRecap = isAddFlow
        ? fullRecap.filter((r) => r.isAddFlowReel === true)
        : fullRecap;
      const untouchedRecap = isAddFlow
        ? fullRecap.filter((r) => r.isAddFlowReel !== true)
        : [];

      if (Number.isInteger(quantity) && quantity > 0) {
        // Separate active and cancelled reels
        const activeReels = existingRecap.filter((r) => r.reelRemoved !== TRUE);
        const cancelledReels = existingRecap.filter(
          (r) => r.reelRemoved === TRUE
        );

        if (quantity > activeReels.length) {
          const needed = quantity - activeReels.length;

          const reelsToRestore = cancelledReels.slice(0, needed);
          const restoredReels = reelsToRestore.map((r) => ({
            ...r,
            reelRemoved: FALSE,
          }));

          const remainingCancelled = cancelledReels.slice(needed);
          const stillNeeded = needed - reelsToRestore.length;

          // Only create truly new reels if cancelled ones are exhausted
          const uniqueId =
            stillNeeded > 0
              ? generateUniqueIdForReels(props.editEvents, addRecapz)
              : 0;

          // Add new reel
          const newReels = Array.from({ length: stillNeeded }, (_, i) => ({
            id: uniqueId + i,
            status: eventBookingStatus.CREATED,
            reelMode: defaultRecapType,
            price: defaultRecapPrice,
            isAddFlowReel: isAddFlow,
            reelName: `${updated[index].eventName} - Recap ${
              existingRecap.length + i + 1
            }`,
            eventOrderItemMap:
              eventId !== ALL_EVENT.id
                ? [
                    {
                      eventId,
                      orderItemId: uniqueId + i,
                      isActive: true,
                    },
                  ]
                : isArrayWithItems(selectedEventIds)
                  ? selectedEventIds.map((id) => ({
                      eventId: id,
                      orderItemId: uniqueId + i,
                      isActive: true,
                    }))
                  : [],
          }));
          updated[index].reelDetail = [
            ...untouchedRecap,
            ...activeReels,
            ...restoredReels,
            ...remainingCancelled,
            ...newReels,
          ];
        } else if (quantity < existingRecap.length) {
          // Smart removal: always keep delivered reels, remove non-delivered first.
          const backendReels = props.editEvents?.reels ?? [];
          const checkDelivered = (r) => {
            const match = backendReels.find(
              (br) =>
                br.id === r.id ||
                br.reelDetail?.id === r.id ||
                br.reelDetail?.orderItemId === r.id
            );
            return !!match?.isReelDelivered;
          };
          const deliveredReels = existingRecap.filter(checkDelivered);
          const nonDeliveredReels = existingRecap.filter(
            (r) => !checkDelivered(r)
          );
          const keepNonDelivered = Math.max(
            0,
            quantity - deliveredReels.length
          );

          const keptReels = [
            ...deliveredReels,
            ...nonDeliveredReels.slice(0, keepNonDelivered),
          ];
          const cancelledNow = nonDeliveredReels
            .slice(keepNonDelivered)
            .map((r) => ({ ...r, reelRemoved: TRUE }));

          updated[index].reelDetail = [
            ...untouchedRecap,
            ...keptReels,
            ...cancelledNow,
            ...cancelledReels,
          ];
        } else {
          // quantity === existingRecap.length, nothing to add or remove
          updated[index].reelDetail = [...untouchedRecap, ...existingRecap];
        }
      } else {
        updated[index].reelDetail = [...untouchedRecap];
      }

      updated[index].quantity = quantity;
    }
    setAddRecapz(updated);
  };

  const handleEventCheckboxChange = (eventId, isChecked, eventName) => {
    if (isAddFlow) {
      setActivatedEvents((prev) => ({ ...prev, [eventId]: isChecked }));
    }
    const updated = [...addRecapz];

    if (isChecked) {
      const existingIndex = updated.findIndex((x) => x.eventId === eventId);

      if (existingIndex !== -1) {
        // Restore — un-remove all reels
        updated[existingIndex] = {
          ...updated[existingIndex],
          isChecked: true,
          reelDetail: updated[existingIndex].reelDetail?.map((r) => ({
            ...r,
            reelRemoved: FALSE,
          })),
        };
      } else {
        updated.push({
          eventId,
          isChecked: true,
          eventName,
          isCombinedEventsReel: eventId === ALL_EVENT.id,
        });
      }
    } else {
      const index = updated.findIndex((x) => x.eventId === eventId);
      if (index !== -1) {
        const fullRecap = Array.isArray(updated[index].reelDetail)
          ? updated[index].reelDetail
          : [];

        // Mirror handleQuantityChange: in the add-flow, unchecking should only
        // affect reels added in this session. Pre-existing reels are left alone.
        const untouchedRecap = isAddFlow
          ? fullRecap.filter((r) => r.isAddFlowReel !== true)
          : [];
        const affectedRecap = isAddFlow
          ? fullRecap.filter((r) => r.isAddFlowReel === true)
          : fullRecap;

        // Never soft-remove delivered reels — same guard as the quantity decrease path
        const backendReels = props.editEvents?.reels ?? [];
        const checkDelivered = (r) => {
          const match = backendReels.find(
            (br) =>
              br.id === r.id ||
              br.reelDetail?.id === r.id ||
              br.reelDetail?.orderItemId === r.id
          );
          return !!match?.isReelDelivered;
        };

        const updatedReelDetail = [
          ...untouchedRecap,
          ...affectedRecap.map((r) =>
            checkDelivered(r) ? r : { ...r, reelRemoved: TRUE }
          ),
        ];

        // Only fully uncheck the event if nothing pre-existing remains.
        // Otherwise keep isChecked: true so confirmHandleNextClick's
        // `item.isChecked === true` filter doesn't drop the whole event
        // (and its untouched reels) from validItems.
        const stillHasUntouchedContent = isAddFlow && untouchedRecap.length > 0;

        updated[index] = {
          ...updated[index],
          isChecked: stillHasUntouchedContent ? true : false,
          reelDetail: updatedReelDetail,
        };
      }
    }

    // Reorder reacap list (combined event always at the end)
    const reordered = getReOrderedRecapList(updated, formState);

    setAddRecapz(reordered);
  };

  const allEventsReel = addRecapz.find((e) => e.eventId === ALL_EVENT.id);

  const handleNextClick = () => {
    //compare existing recaps with updated recaps and show confirmation model if anything has changed
    if (props.isEditEvent || isAddUpdateRecap) {
      const isIdenticalRecapz = isAddRecapzIdentical(
        addRecapz,
        addRecapzBackup
      );

      if (!isIdenticalRecapz && !isAddFlow) {
        setShowModal(true);
        setRecapBriefConfirmModalMessage(
          `${messages.REGENERATE_EDIT_BRIEF_CONFIRMATION_MESSAGE}`
        );
        return;
      }
    }
    confirmHandleNextClick();
  };

  const confirmHandleNextClick = () => {
    // Filter valid items
    let validItems = addRecapz.filter((item) => item.isChecked === true);

    if (isAddFlow) {
      const hasAnyNewReel = validItems.some((item) =>
        (item.reelDetail ?? []).some(
          (r) => r.isAddFlowReel === true && r.reelRemoved !== TRUE
        )
      );

      if (!hasAnyNewReel) {
        customToast(t("please_select_quantity"), toast.TYPE.ERROR);
        return;
      }
    }

    if (validItems.length === 0) {
      customToast(t("please_select_quantity"), toast.TYPE.ERROR);
      return;
    }

    if (validItems.some((item) => !item.quantity || item.quantity <= 0)) {
      customToast(t("quantity_is_not_selected"), toast.TYPE.ERROR);
      return;
    }

    // Check combined reel have at least one events is selected
    const combinedReel = validItems.find((item) => item.isCombinedEventsReel);
    if (combinedReel) {
      const hasEventsSelected = combinedReel.reelDetail?.some(
        (rd) =>
          isArrayWithItems(rd.eventOrderItemMap) &&
          rd.eventOrderItemMap.filter((item) => item.isActive ?? true).length >=
            2
      );

      if (!hasEventsSelected) {
        customToast(
          messages.PLEASE_SELECT_EVENTS_FOR_COMBINED_REEL,
          toast.TYPE.ERROR
        );
        return;
      }
    }
    // Snapshot removed reels to localStorage
    const removedSnapshot = addRecapz
      .map((item) => ({
        eventId: item.eventId,
        reelDetail: (item.reelDetail ?? []).filter(
          (r) => r.reelRemoved === TRUE
        ),
      }))
      .filter((item) => item.reelDetail.length > 0);

    saveToLocalStorage(localStorageKeys.ADD_CANCELLED_RECAPZ, removedSnapshot);
    const cleanItems = validItems.map((item) => ({
      ...item,
      reelDetail: (item.reelDetail ?? []).filter((r) => r.reelRemoved !== TRUE),
    }));

    setAddRecapz(cleanItems);
    // Proceed to next step
    handleActiveStep(ADD_RECAPZ_STEPS.SELECT_RECAP_TYPE);
  };

  const handleConfirm = () => {
    props.setRegenerateEditBriefs(true);
    confirmHandleNextClick();
    setRecapBriefConfirmModalMessage(EMPTY_STRING);
    setShowModal(false);
  };

  // options of combined events selection dropdown from formState(events)
  const eventOptions = formState?.map((event) => ({
    value: event.id,
    label: event.eventName,
  }));

  // get selected events from addRecapz in case of combined event reel
  const selectedEventIds =
    addRecapz
      ?.find((recap) => recap.isCombinedEventsReel)
      ?.reelDetail?.[0]?.eventOrderItemMap?.filter(
        (e) => e.isActive === undefined || e.isActive === true
      )
      ?.map((e) => e.eventId) || [];

  // handle selection change of combined events dropdown
  const handleEventsSelect = (selectedOptions = []) => {
    const updatedAddRecapz = addRecapz.map((recap) => {
      if (!recap.isCombinedEventsReel || !isArrayWithItems(recap?.reelDetail))
        return recap;

      const updatedReelDetail = recap.reelDetail.map((detail) => {
        const existingMap = detail.eventOrderItemMap ?? [];

        //Build updated map — preserving existing data or restoring from combinedRecapsEventMap
        const updatedMap = selectedOptions.map((eventId) => {
          // Try to find existing mapping first
          const existing = existingMap.find((x) => x.eventId === eventId);

          return {
            ...(existing || {}),
            eventId,
            orderItemId: detail.id,
            isActive: true,
          };
        });

        // Merge active + inactive mappings
        const finalMap = [...updatedMap];

        return {
          ...detail,
          eventOrderItemMap: finalMap,
        };
      });

      return { ...recap, reelDetail: updatedReelDetail };
    });

    props.setAddRecapz(updatedAddRecapz);
  };

  const addRecapzBackup = useMemo(() => {
    const reels = props?.editEvents?.reels ?? [];
    return transformReelsInEditEvent(reels, ALL_EVENT.id);
  }, [props.editEvents]);

  // Delivered reel info per event (memoised)
  const deliveredInfoMap = useMemo(() => {
    const map = {};
    if (formState) {
      formState.forEach((event) => {
        map[event.id] = getDeliveredReelInfo(event.id, props.editEvents);
      });
    }
    map[ALL_EVENT.id] = getDeliveredReelInfo(ALL_EVENT.id, props.editEvents);
    return map;
  }, [formState, props.editEvents]);

  // Map of eventId -> whether that event already has all 5 reel slots
  // booked (from editEvents.reels, i.e. backend truth). Max slot count
  // comes from the highest value in the quantity options config, so it
  // stays in sync if that list ever changes.
  const maxRecapOptionValue = useMemo(() => {
    const options = window.config?.eventRecapQuantityOption ?? [];
    return options.reduce(
      (max, opt) => Math.max(max, parseInt(opt.value, TEN) || 0),
      0
    );
  }, []);

  const fullyBookedMap = useMemo(() => {
    const map = {};
    if (formState) {
      formState.forEach((event) => {
        const bookedCount = getBookedReelCount(event.id, props.editEvents);
        map[event.id] = {
          bookedCount,
          isFullyBooked: bookedCount >= maxRecapOptionValue,
        };
      });
    }
    const combinedBookedCount = getBookedReelCount(
      ALL_EVENT.id,
      props.editEvents
    );
    map[ALL_EVENT.id] = {
      bookedCount: combinedBookedCount,
      isFullyBooked: combinedBookedCount >= maxRecapOptionValue,
    };
    return map;
  }, [formState, props.editEvents, maxRecapOptionValue]);

  const getDisplayQuantity = (item) => {
    if (!item) return EMPTY_STRING;
    if (!isAddFlow) return item.quantity;

    const activeAddFlowReels = (item.reelDetail ?? []).filter(
      (r) => r.reelRemoved !== TRUE && r.isAddFlowReel === true
    );
    return activeAddFlowReels.length || EMPTY_STRING;
  };

  const getIsVisuallyChecked = (item) => {
    if (!item) return false;
    if (!isAddFlow) return item.isChecked || false;

    const hasActiveNewReel = (item.reelDetail ?? []).some(
      (r) => r.reelRemoved !== TRUE && r.isAddFlowReel === true
    );
    return Boolean(item.isChecked) && hasActiveNewReel;
  };

  const showQuantitySelector = (item, eventId) => {
    if (!item?.isChecked) return false;
    if (!isAddFlow) return true;

    const hasActiveNewReel = (item.reelDetail ?? []).some(
      (r) => r.reelRemoved !== TRUE && r.isAddFlowReel === true
    );
    return hasActiveNewReel || Boolean(activatedEvents[eventId]);
  };

  const isEventActivated = (item, eventId) => {
    if (Object.prototype.hasOwnProperty.call(activatedEvents, eventId)) {
      return activatedEvents[eventId];
    }
    // No explicit click yet this session — default to "on" only if there's
    // already an active reel added through this flow (e.g. returning to this
    // step after adding one).
    const hasActiveNewReel = (item?.reelDetail ?? []).some(
      (r) => r.reelRemoved !== TRUE && r.isAddFlowReel === true
    );
    return hasActiveNewReel;
  };

  const {
    hasDelivered: combinedHasDelivered,
    deliveredCount: combinedDeliveredCount,
  } = deliveredInfoMap[ALL_EVENT.id] ?? {};

  const isGoBackDisabled = Boolean(
    formState?.some((event) => deliveredInfoMap[event.id]?.hasDelivered) ||
    deliveredInfoMap[ALL_EVENT.id]?.hasDelivered
  );

  return (
    <div className="select-event-quantity-container col-12 col-md-8 col-lg-6 margin-0-auto">
      {isUpdateRecap ? (
        <p className="add-recapz-heading label-content-600 mb-2">
          Edits you have booked for your event.
        </p>
      ) : (
        <p className="add-recapz-heading label-content-600 mb-2">
          which bookings do you want to add edits for?
        </p>
      )}

      <AvForm id="add-recapz" autoComplete="off">
        <div id="form-content" className="px-md-5 pt-4 text-left">
          {formState?.map((event) => {
            const selectedEvent = props.addRecapz.find(
              (e) => e.eventId === event.id
            );
            const { hasDelivered, deliveredCount } =
              deliveredInfoMap[event.id] ?? {};

            return (
              <div
                key={event.id}
                className="d-md-flex justify-content-between d-sm-block mt-2"
              >
                <div
                  className={`d-flex align-items-center quanity-checkbox-container`}
                >
                  <input
                    type="checkbox"
                    checked={
                      isAddFlow && fullyBookedMap[event.id]?.isFullyBooked
                        ? false
                        : isAddFlow
                          ? isEventActivated(selectedEvent, event.id)
                          : selectedEvent?.isChecked || false
                    }
                    disabled={
                      hasDelivered ||
                      isUpdateRecap ||
                      (isAddFlow && fullyBookedMap[event.id]?.isFullyBooked)
                    }
                    onChange={(e) =>
                      handleEventCheckboxChange(
                        event.id,
                        e.target.checked,
                        event.eventName
                      )
                    }
                  />

                  <span className="event-name pl-2">{event.eventName}</span>
                </div>
                <div className="d-flex align-items-center pl-1">
                  {(isAddFlow
                    ? isEventActivated(selectedEvent, event.id)
                    : selectedEvent?.isChecked) && (
                    <>
                      <span className="quantity pr-2">quantity:</span>
                      <InputGroupWrapper>
                        <AvField
                          type="select"
                          name={"quantity" + event?.id}
                          className={
                            "pr-5 pr-md-2 select-dropdown recap-quantity-dropdown"
                          }
                          value={getDisplayQuantity(selectedEvent)}
                          onChange={(e) =>
                            handleQuantityChange(event.id, e.target.value)
                          }
                        >
                          <option selected disabled value="">
                            select
                          </option>
                          {window.config?.eventRecapQuantityOption?.map(
                            (option) => (
                              <option
                                key={option.id}
                                value={option.value}
                                disabled={
                                  isUpdateRecap ||
                                  (hasDelivered &&
                                    parseInt(option.value, TEN) <
                                      deliveredCount) ||
                                  (isAddFlow &&
                                    parseInt(option.value, TEN) >
                                      maxRecapOptionValue -
                                        (fullyBookedMap[event.id]
                                          ?.bookedCount ?? 0))
                                }
                              >
                                {option.label}
                              </option>
                            )
                          )}
                        </AvField>
                        <ChevronDownIcon />
                      </InputGroupWrapper>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {formState?.length !== 1 && (
            <div className="d-md-flex justify-content-between d-sm-block mt-2">
              <div
                className={
                  "d-flex align-items-center quanity-checkbox-container"
                }
              >
                <input
                  type="checkbox"
                  name="allEventCheckbox"
                  checked={
                    isAddFlow && fullyBookedMap[ALL_EVENT.id]?.isFullyBooked
                      ? false
                      : isAddFlow
                        ? isEventActivated(allEventsReel, ALL_EVENT.id)
                        : allEventsReel?.isChecked || false
                  }
                  disabled={
                    combinedHasDelivered ||
                    isUpdateRecap ||
                    (isAddFlow && fullyBookedMap[ALL_EVENT.id]?.isFullyBooked)
                  }
                  onChange={(e) =>
                    handleEventCheckboxChange(
                      ALL_EVENT.id,
                      e.target.checked,
                      ALL_EVENT.name
                    )
                  }
                />
                <span className="event-name pl-2">{ALL_EVENT.name}</span>
                <div className="pl-1">
                  <CustomTooltip
                    id="tooltip"
                    title="a combined edit lets you create one or more recap videos using footage from multiple events."
                    arrow
                    placement="right"
                  >
                    <span tabIndex="0">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="info-icon"
                      />
                    </span>
                  </CustomTooltip>
                </div>
              </div>
              <div className="d-flex align-items-center">
                {(isAddFlow
                  ? isEventActivated(allEventsReel, ALL_EVENT.id)
                  : allEventsReel?.isChecked) && (
                  <>
                    <span className="quantity pr-2">quantity:</span>
                    <InputGroupWrapper>
                      <AvField
                        type="select"
                        name={"quantity" + ALL_EVENT.id}
                        className={
                          "pr-5 pr-md-2 select-dropdown recap-quantity-dropdown"
                        }
                        value={getDisplayQuantity(allEventsReel)}
                        onChange={(e) =>
                          handleQuantityChange(ALL_EVENT.id, e.target.value)
                        }
                      >
                        <option selected disabled value="">
                          select
                        </option>
                        {window.config?.eventRecapQuantityOption?.map(
                          (option) => (
                            <option
                              key={option.id}
                              value={option.value}
                              disabled={
                                isUpdateRecap ||
                                (combinedHasDelivered &&
                                  parseInt(option.value, TEN) <
                                    combinedDeliveredCount) ||
                                (isAddFlow &&
                                  parseInt(option.value, TEN) >
                                    maxRecapOptionValue -
                                      (fullyBookedMap[ALL_EVENT.id]
                                        ?.bookedCount ?? 0))
                              }
                            >
                              {option.label}
                            </option>
                          )
                        )}
                      </AvField>
                      <ChevronDownIcon />
                    </InputGroupWrapper>
                  </>
                )}
              </div>
            </div>
          )}

          {allEventsReel?.isChecked &&
            isArrayWithItems(allEventsReel?.reelDetail) &&
            formState?.length > 1 && (
              <div className="mt-2">
                <MultiSelectDropdown
                  options={eventOptions}
                  selectedValues={selectedEventIds}
                  onSelectionChange={handleEventsSelect}
                  label={`${messages.SELECT_EVENTS}:`}
                  valueKey="value"
                  placeholder="Select events..."
                  disabled={
                    combinedHasDelivered ||
                    (isAddUpdateRecap &&
                      (fullyBookedMap[ALL_EVENT.id]?.bookedCount ?? 0) > 0)
                  }
                />
              </div>
            )}
        </div>
      </AvForm>
      <div className="d-flex justify-content-center sticky-button-row mt-4">
        {!isAddUpdateRecap && (
          <button
            type="button"
            disabled={isGoBackDisabled}
            className="event-back-btn button-common button-outline btn-text-grey width-50"
            onClick={() => handleActiveStep(ADD_RECAPZ_STEPS.ADD_BOOKING)}
          >
            <BackIcon />
            <span className="pl-1">{t("go_back")}</span>
          </button>
        )}
        <button
          type="button"
          className="button-common button-fill width-50 ml-3"
          onClick={handleNextClick}
        >
          {t("next")}
        </button>
      </div>

      <ConfirmationModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        dialogText={`${recapBriefConfirmModalMessage}`}
        maxWidth="xs"
      />
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  addRecapz: state.eventBooking.addRecapz,
  recapTypes: state.eventBooking.recapTypes,
  isEditEvent: state.eventBooking.isEditEvent,
  editEvents: state.eventBooking.editEvents,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  setRegenerateEditBriefs: (data) =>
    dispatch(eventBookingActions.setRegenerateEditBriefs(data)),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SelectEventQuantity);
